import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminService from '../../services/adminService';

// ============================================================================
// ASYNC THUNKS
// ============================================================================

export const createAdmin = createAsyncThunk(
  'admin/createAdmin',
  async ({ practiceId, adminData }, { rejectWithValue }) => {
    try {
      const response = practiceId 
        ? await adminService.createAdmin(practiceId, adminData)
        : await adminService.createSystemAdmin(adminData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create admin');
    }
  }
);

export const fetchAdmins = createAsyncThunk(
  'admin/fetchAdmins',
  async ({ practiceId, params = {}, forceRefresh = false }, { rejectWithValue }) => {
    try {
      // Add timestamp to force fresh data fetch
      const freshParams = forceRefresh ? { ...params, _t: Date.now() } : params;
      const response = practiceId 
        ? await adminService.getAdmins(practiceId, freshParams)
        : await adminService.getAllAdmins(freshParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admins');
    }
  }
);

export const fetchAdmin = createAsyncThunk(
  'admin/fetchAdmin',
  async ({ practiceId, adminId }, { rejectWithValue }) => {
    try {
      const response = practiceId 
        ? await adminService.getAdmin(practiceId, adminId)
        : await adminService.getSystemAdmin(adminId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admin');
    }
  }
);

export const updateAdmin = createAsyncThunk(
  'admin/updateAdmin',
  async ({ practiceId, adminId, adminData }, { rejectWithValue }) => {
    try {
      const response = practiceId 
        ? await adminService.updateAdmin(practiceId, adminId, adminData)
        : await adminService.updateSystemAdmin(adminId, adminData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update admin');
    }
  }
);

export const deleteAdmin = createAsyncThunk(
  'admin/deleteAdmin',
  async ({ practiceId, adminId }, { rejectWithValue }) => {
    try {
      const response = practiceId 
        ? await adminService.deleteAdmin(practiceId, adminId)
        : await adminService.deleteSystemAdmin(adminId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.status === 404 
        ? 'Admin not found or has already been deleted'
        : error.response?.data?.message || 'Failed to delete admin';
      return rejectWithValue(errorMessage);
    }
  }
);

// ============================================================================
// SLICE DEFINITION
// ============================================================================

const initialState = {
  admins: [],
  /** Per-practice admin lists — avoids refetch when switching tabs/routes back */
  adminsByPracticeId: {},
  practiceAdminFetched: {},
  globalAdminsFetched: false,
  currentAdmin: null,
  pagination: {
    current_page: 1,
    per_page: 15,
    total: 0,
    total_pages: 0
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentAdmin: (state) => {
      state.currentAdmin = null;
    },
    resetState: (state) => {
      return { ...initialState };
    },
    invalidatePracticeAdminCache: (state, action) => {
      const pid = action.payload;
      if (pid == null) return;
      const key = String(pid);
      delete state.practiceAdminFetched[key];
      delete state.adminsByPracticeId[key];
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Admin
      .addCase(createAdmin.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.isCreating = false;
        if (!state.admins) {
          state.admins = [];
        }
        const adminData = action.payload.data?.admin || action.payload.data || action.payload;
        
        // Optimistically update the list if it's already in the state
        state.admins.unshift(adminData);
        const pid = action.meta?.arg?.practiceId;
        if (pid != null) {
          const key = String(pid);
          if (state.adminsByPracticeId[key]) {
            state.adminsByPracticeId[key].unshift(adminData);
          }
          // Invalidate practice cache to force a fresh fetch from server on next visit
          delete state.practiceAdminFetched[key];
        } else {
          // Invalidate global cache for system admins
          state.globalAdminsFetched = false;
        }
        
        state.pagination.total += 1;
        state.error = null;
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      
      // Fetch Admins
      .addCase(fetchAdmins.pending, (state, action) => {
        const pid = action.meta?.arg?.practiceId;
        const forceRefresh = action.meta?.arg?.forceRefresh;
        
        if (!forceRefresh) {
          if (pid != null && state.practiceAdminFetched[String(pid)]) return;
          if (pid == null && state.globalAdminsFetched) return;
        }
        
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle nested data structure from backend
        let admins = [];
        if (action.payload.data && action.payload.data.admins) {
          admins = action.payload.data.admins;
          state.pagination = action.payload.data.pagination || state.pagination;
        } else {
          admins = action.payload.data || [];
        }
        // Filter out deleted admins (double-check on frontend)
        const list = admins.filter(admin => !admin.deleted_at);
        
        const pid = action.meta?.arg?.practiceId;
        if (pid != null) {
          const key = String(pid);
          state.adminsByPracticeId[key] = list;
          state.practiceAdminFetched[key] = true;
        } else {
          // Only update global admins list if we are fetching all admins (System level)
          state.admins = list;
          state.globalAdminsFetched = true;
        }
        state.error = null;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Fetch Single Admin
      .addCase(fetchAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAdmin = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(fetchAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Update Admin
      .addCase(updateAdmin.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.isUpdating = false;
        const pid = action.meta?.arg?.practiceId;
        const key = pid != null ? String(pid) : null;
        const syncList = (arr) => {
          if (!action.payload.data || !arr) return;
          const index = arr.findIndex(admin => admin.id === action.payload.data.id);
          if (index !== -1) {
            arr[index] = { ...arr[index], ...action.payload.data };
          }
        };
        if (action.payload.data && state.admins && Array.isArray(state.admins)) {
          syncList(state.admins);
        }
        if (key && state.adminsByPracticeId[key]) {
          syncList(state.adminsByPracticeId[key]);
        }
        // Invalidate appropriate cache to force re-fetch
        if (key) {
          delete state.practiceAdminFetched[key];
        } else {
          state.globalAdminsFetched = false;
        }
        // Update current admin if it's the same
        if (action.payload.data && state.currentAdmin && state.currentAdmin.id === action.payload.data.id) {
          state.currentAdmin = { ...state.currentAdmin, ...action.payload.data };
        }
        state.error = null;
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      
      // Delete Admin
      .addCase(deleteAdmin.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.isDeleting = false;
        const adminId = action.meta.arg.adminId;
        const pid = action.meta.arg.practiceId;
        const key = pid != null ? String(pid) : null;
        if (state.admins && Array.isArray(state.admins)) {
          state.admins = state.admins.filter(admin => admin.id !== adminId);
          state.pagination.total -= 1;
        }
        if (key && state.adminsByPracticeId[key]) {
          state.adminsByPracticeId[key] = state.adminsByPracticeId[key].filter(a => a.id !== adminId);
        }
        state.error = null;
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  }
});

export const {
  clearError,
  clearCurrentAdmin,
  resetState,
  invalidatePracticeAdminCache
} = adminSlice.actions;

export default adminSlice.reducer;